import React, { useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { useTable, useFilters } from "react-table";
import TableWarnings from "../../tableWarnings";
import styled from "styled-components";
import { getTableColumns } from "./tableColumns";
import { startsWithFilter } from "./ColumnFilter";
import {
  ACCESS_LEVELS,
  BLOCK_STATUS,
  SPECIAL_FLAGS,
  SCROLL_CONFIG,
} from "../constants";
import AuditPopup from "./AuditDetails";
import ErrorModal from "./Modals/ErrorModal";
import WarningModal from "./Modals/WarningModal";
import RefreshModal from "./Modals/RefreshModal";
import CancelChangesModal from "./Modals/CancelChangesModal";
import Select from "react-select";
import { format } from "date-fns";
import { useTableData } from "../../../hooks/useTableData";
import { updateAssignment } from "../../../utils/updateAssignment";
import { isBeforePullinTime } from "../../../utils/isBeforePullInTime";
import { verifyTableRowsForUpdate } from "../../../utils/TableHelpers/verifyTableRowsForUpdate";
import { applyDefaultLocation } from "../../../utils/TableHelpers/applyDefaultLocation";
import { EDITABLE_COLUMNS } from "./Renderables/shared.js";

// Helper function to get relative time string
// Maps a failed updateAssignment response from apiClient into a user-facing
// error string. 401 means the session expired; 403 means the user lacks write
// access on the division they're editing (e.g., a READ_ONLY user). Anything
// else is treated as a generic comms failure.
const getSaveErrorMessage = (response) => {
  if (response.status === 401) {
    return "Your session has expired. Please log in again. Your changes have NOT been saved.";
  }
  if (response.status === 403) {
    return "You don't have permission to save changes for this division. Your changes have NOT been saved.";
  }
  return "We are having difficulties communicating with the server. Your changes have NOT been saved.";
};

const getRelativeTimeString = (date) => {
  if (!date) return "";
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);

  if (diffMins < 1) {
    return { text: "Now", isOverAnHour: false };
  } else if (diffMins < 60) {
    return {
      text: `${diffMins} minute${diffMins === 1 ? "" : "s"} ago`,
      isOverAnHour: false,
    };
  } else {
    return {
      text: `over ${diffHours} hour${diffHours === 1 ? "" : "s"} ago`,
      isOverAnHour: true,
    };
  }
};

const ToolbarRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 6px 10px;
  align-items: center;
  font-size: 0.85em;
  font-weight: 700;

  label {
    font-weight: 700;
  }
`;

const ToolbarDivider = styled.div`
  width: 1px;
  height: 20px;
  background-color: #ccc;
`;

const MessageGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: stretch;
  padding: 4px 10px 6px;
`;

const AlertBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.85em;
  font-weight: 600;
  border-left: 4px solid;

  &.error {
    background: #fef2f2;
    border-left-color: var(--clr-red-dark);
    color: #7f1d1d;
  }

  &.success {
    background: #f0fdf4;
    border-left-color: var(--clr-green-dark);
    color: #14532d;
  }

  &.warning {
    background: #fffbeb;
    border-left-color: #d97706;
    color: #78350f;
  }

  .alert-link-button {
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    font: inherit;
    color: inherit;
    cursor: pointer;
    text-decoration: underline;
  }
`;

const DismissAllButton = styled.button`
  background: none;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8em;
  font-weight: 500;
  padding: 3px 14px;
  margin-left: 4px;
  color: #555;
  white-space: nowrap;
  transition: all 0.15s;

  &:hover {
    background: #f3f4f6;
    border-color: #999;
    color: #333;
  }
`;

const MainTableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const TableContainer = styled.div`
  padding: 0.2rem;
  overflow: auto;
  width: 100%;
  flex: 1;
  min-height: 0;

  table {
    border-spacing: 0;
    border: 1px solid #d5d5d5;
    width: 100%;
    table-layout: auto;

    @media (max-width: 1400px) {
      min-width: 1400px;
    }

    thead {
      position: sticky;
      top: 0;
      z-index: 1;

      &::before {
        content: "";
        position: absolute;
        top: -5px;
        left: 0;
        right: 0;
        height: 5px;
        background-color: #fff;
      }
    }

    th,
    td {
      margin: 0;
      padding: 0.5rem;
      border-bottom: 1px solid #d5d5d5;
      border-right: 1px solid #d5d5d5;
      white-space: nowrap;
    }

    th:last-child,
    td:last-child {
      border-right: 1px solid #d5d5d5;
    }

    tr:last-child td {
      border-bottom: 0;
    }
  }
`;

const MainTable = ({
  assignmentDate,
  blockMessages,
  boxJumpTo,
  clearAllFilters,
  data: rawData,
  errorMessage,
  exceptionFilters,
  getData,
  isSpecial,
  lastUpdated,
  loadingTableData = false,
  onVehicleFetchFailed,
  pulloutDivision,
  rowFilter,
  selected,
  selectedVehicles,
  setApiErrors,
  setBlockMessages,
  setBoxJumpTo,
  setErrorMessage,
  setExceptionFilters,
  setSelectedVehicles,
  setSuccessMessage,
  setUpdateErrors,
  setUpdatedData,
  setVehiclesLoading,
  successMessage,
  tableFetchFailed = false,
  TableContext,
  updatedData,
  updateErrors,
  userDivisions,
  vehicleDivision,
}) => {
  // Use centralized data fetching hook
  const {
    vehicles,
    duplicateVehicles,
    locations,
    tradeReasons,
    exceptions,
    boxNumbers,
    loadingData,
    setLoadingData,
  } = useTableData({
    assignmentDate,
    vehicleDivision,
    pulloutDivision,
    isSpecial,
    setApiErrors,
    setErrorMessage,
    onVehicleFetchFailed,
    setVehiclesLoading,
  });

  // When a division has one real location, default it onto rows that have none.
  // Applied to source rows so it stays out of the dirty check and only saves
  // when the row is otherwise edited.
  const data = useMemo(
    () => applyDefaultLocation(rawData, locations),
    [rawData, locations],
  );

  const [displayUAR, setDisplayUAR] = useState(false);
  const [showAuditDetailsModal, setShowAuditDetailsModal] = useState(false);
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [, setTimeUpdate] = useState(0); // Trigger re-render for relative time
  const [isSaving, setIsSaving] = useState(false);
  // Synchronous guard: blocks re-entrant saves even before the disabled button re-renders.
  const savingRef = useRef(false);
  const tableBodyRef = useRef(null);
  const rowRefs = useRef([]);
  // Defensive: pulloutDivision can be `null` if a user is authenticated but
  // none of their assigned divisions are present in the current /busDivisions
  // response (e.g., a contractor account assigned to divisions the upstream
  // doesn't currently know about). The Table-level guard should catch this
  // upstream, but guard here too so a single render with a stale prop value
  // can't crash the page. Treat "no matching user division" as view-only —
  // the safe default; write actions are gated by `!viewOnly` downstream.
  const userDivision = userDivisions.find(
    (division) =>
      division?.divisionId != null &&
      pulloutDivision != null &&
      division.divisionId.toString() === pulloutDivision.toString(),
  );
  const viewOnly = userDivision?.accessLevel !== ACCESS_LEVELS.READ_WRITE;

  const firstEmptyRow = data.findIndex((row) => row.lastVehicle === null);
  const targetIndex = Math.max(
    0,
    firstEmptyRow - SCROLL_CONFIG.FIRST_EMPTY_ROW_OFFSET,
  );
  const targetRow = rowRefs.current[targetIndex];

  useEffect(() => {
    if (
      tableBodyRef.current &&
      targetRow &&
      firstEmptyRow > SCROLL_CONFIG.FIRST_EMPTY_ROW_OFFSET &&
      !boxJumpTo
    ) {
      requestAnimationFrame(() => {
        const tableBody = tableBodyRef.current;
        if (tableBody) {
          const rowTop =
            targetRow.getBoundingClientRect().top -
            tableBody.getBoundingClientRect().top;
          tableBody.scrollTo({
            top: tableBody.scrollTop + rowTop,
            behavior: SCROLL_CONFIG.SCROLL_BEHAVIOR,
          });
        }
      });
    }
  }, [boxJumpTo, firstEmptyRow, targetRow]);

  // Handle box jump: scroll to selected row and focus Bus input
  useEffect(() => {
    if (selected >= 0 && rowRefs.current[selected] && tableBodyRef.current) {
      requestAnimationFrame(() => {
        const tableBody = tableBodyRef.current;
        const selectedRow = rowRefs.current[selected];
        // Anchor the scroll a few rows above the target so QA gets context
        // (e.g. jump to 75 → show 72, 73, 74, 75). Clamped, so jumping near the
        // top just goes to the top. The target row stays the highlighted one.
        const anchorIndex = Math.max(
          0,
          selected - SCROLL_CONFIG.JUMP_CONTEXT_ROWS,
        );
        const anchorRow = rowRefs.current[anchorIndex] || selectedRow;
        if (tableBody && selectedRow) {
          // Place the anchor row directly beneath the sticky header by letting
          // the browser do the scrolling: scroll-margin-top reserves the header's
          // height and block:"start" aligns it to the top of the scroll
          // container. This is deterministic and immune to the rect/scrollTop
          // unit drift of manual math (which zoom makes worse); the browser also
          // clamps at the bottom, so a low row scrolls up as far as it can.
          const headerEl = tableBody.querySelector("thead");
          const headerHeight = headerEl
            ? headerEl.getBoundingClientRect().height
            : 0;
          anchorRow.style.scrollMarginTop = `${headerHeight}px`;
          anchorRow.scrollIntoView({ block: "start" });

          // Focus the Bus input for this row after scroll completes.
          // preventScroll: focusing must not trigger the browser's native
          // scroll-into-view, which ignores the sticky header and fights our
          // deliberate scroll position.
          setTimeout(() => {
            const busInput = document.getElementById(`lastVehicle-${selected}`);
            if (busInput) {
              const inputElement = busInput.querySelector("input");
              if (inputElement) {
                inputElement.focus({ preventScroll: true });
              }
            }
          }, 300); // Small delay to allow scroll to complete
        }
      });
    }
  }, [selected]);

  // Update relative time display every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeUpdate((prev) => prev + 1);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const togglePopup = () => {
    setShowAuditDetailsModal(!showAuditDetailsModal);
  };

  const handleSelectedVehicles = (rowIndex, value) => {
    const formattedValue = `${rowIndex}-${value}`;
    const isRowSelected = selectedVehicles.some((value) =>
      value.startsWith(`${rowIndex}-`),
    );

    const updatedSelectedVehicles = isRowSelected
      ? selectedVehicles.map((value) =>
          value.startsWith(`${rowIndex}-`) ? formattedValue : value,
        )
      : [...selectedVehicles, formattedValue];

    setSelectedVehicles(updatedSelectedVehicles);
  };

  // Verifies that any rows with changes have both the lastVehicle and pulloutLocation filled out.

  const handleGenerateWarningMessages = ({
    duplicateBus,
    foreignDivision,
    secondaryVehicleType,
    vehicleTypeMismatch,
  }) => {
    const warnings = {
      duplicateBus: "Duplicate Bus",
      foreignDivision: "Foreign Division",
      secondaryVehicleType:
        "Assigned a vehicle that is not the primary or secondary vehicle type",
      vehicleTypeMismatch: "Vehicle Type Mismatch",
    };

    return Object.entries({
      duplicateBus,
      foreignDivision,
      secondaryVehicleType,
      vehicleTypeMismatch,
    }).reduce((messages, [key, value]) => {
      if (value === SPECIAL_FLAGS.YES) {
        messages.push(warnings[key]);
      }
      return messages;
    }, []);
  };

  // If updated table rows have been filled out correctly, map through the updated rows to update each assignment.
  const handleUpdateTable = async () => {
    if (savingRef.current) return;

    const validTableRows = verifyTableRowsForUpdate(
      setErrorMessage,
      setSuccessMessage,
      setBlockMessages,
      setUpdateErrors,
      updatedData,
    );
    if (!validTableRows) return;

    savingRef.current = true;
    setIsSaving(true);
    try {
      await submitTableUpdate();
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  // Map dirty rows into the snake_case payload the /updateAssignment API expects.
  const buildTransformedRows = (rowsToBeUpdated, apiDate) =>
    rowsToBeUpdated.map(
      ({
        blockAssignmentId,
        scheduledPulloutTimeString,
        tradeReason,
        pulloutVarianceReason,
        noteToOperator,
        signOutComments,
        versionTimestamp,
        boxNumber,
        lastVehicle,
        pulloutLocation,
        earlyPullIn,
        scheduledPullinTimeFmt,
      }) => {
        let checkedEarlyPullIn = SPECIAL_FLAGS.NO;
        if (earlyPullIn === SPECIAL_FLAGS.YES) {
          const earlyPullInIsValid = isBeforePullinTime(scheduledPullinTimeFmt);
          checkedEarlyPullIn = earlyPullInIsValid
            ? SPECIAL_FLAGS.YES
            : SPECIAL_FLAGS.NO;
        }

        return {
          assignment_Date: apiDate,
          box_number: boxNumber,
          block_assignment_id: blockAssignmentId,
          equip_no: lastVehicle,
          location: pulloutLocation,
          note_to_operator: noteToOperator,
          pullout_division: pulloutDivision,
          pullout_time: scheduledPulloutTimeString,
          pullout_variance: pulloutVarianceReason,
          pullin_block: checkedEarlyPullIn,
          sign_out_comments: signOutComments,
          trade_reason: tradeReason,
          version_timestamp: versionTimestamp,
        };
      },
    );

  // On a partial (mixed) failure, refetch keeping only the rows that failed.
  const refetchMixedErrors = async (blockList, rowsToBeUpdated) => {
    const isMixed = blockList.some(
      (block) => block.blockStatus !== BLOCK_STATUS.ERROR,
    );
    if (!isMixed) return;

    const failedIds = new Set(
      blockList
        .filter((block) => block.blockStatus === BLOCK_STATUS.ERROR)
        .map((block) => block.blockAssignmentId.toString()),
    );
    const maintainedUpdatedData = rowsToBeUpdated.filter((row) =>
      failedIds.has(row.blockAssignmentId.toString()),
    );
    await getData(maintainedUpdatedData);
  };

  // Surface per-block errors and warnings returned alongside a successful save.
  const applyBlockMessages = (blockList, rowsToBeUpdated) => {
    const blockWarnings = blockList.filter((block) =>
      [
        BLOCK_STATUS.SUCCESS_WITH_WARNING,
        BLOCK_STATUS.ERROR,
        BLOCK_STATUS.VALIDATION_ERROR,
      ].includes(block.blockStatus),
    );

    blockWarnings.forEach((block) => {
      const matchingRow = rowsToBeUpdated.find(
        (row) => row.blockAssignmentId.toString() === block.blockAssignmentId,
      );

      if (
        block.blockStatus === BLOCK_STATUS.ERROR ||
        block.blockStatus === BLOCK_STATUS.VALIDATION_ERROR
      ) {
        setUpdateErrors((prevMessages) => [
          ...prevMessages,
          {
            boxNumber: matchingRow.boxNumber,
            blockError: block.blockMessage,
          },
        ]);
      } else if (matchingRow) {
        const blockWarningMessages = handleGenerateWarningMessages({
          duplicateBus: block.duplicateBus,
          foreignDivision: block.foreignDivision,
          secondaryVehicleType: block.secondaryVehicleType,
          vehicleTypeMismatch: block.vehicleTypeMismatch,
        });

        setBlockMessages((prevMessages) => [
          ...prevMessages,
          {
            boxNumber: matchingRow.boxNumber,
            blockWarnings: blockWarningMessages,
          },
        ]);
      }
    });
  };

  const handleSuccessResponse = async (response, rowsToBeUpdated) => {
    setLoadingData(true);
    const { status, blockList, message } = response.data;

    if ([BLOCK_STATUS.SUCCESS, BLOCK_STATUS.SUCCESS_WITH_WARNING].includes(status)) {
      await getData();
    }

    if (status === BLOCK_STATUS.VALIDATION_ERROR && !blockList) {
      setErrorMessage(`Error when saving changes - ${message}`);
      setLoadingData(false);
      return;
    }

    if (status === BLOCK_STATUS.ERROR) {
      await refetchMixedErrors(blockList, rowsToBeUpdated);
    }

    applyBlockMessages(blockList, rowsToBeUpdated);
    setSuccessMessage(`${message}`);
    setLoadingData(false);
  };

  const submitTableUpdate = async () => {
    const rowsToBeUpdated = updatedData.filter((row) => row.updated);
    const apiDate = format(assignmentDate, "dd-MMM-yyyy");
    const transformedRows = buildTransformedRows(rowsToBeUpdated, apiDate);

    const response = await updateAssignment(transformedRows);

    if (response.success) {
      await handleSuccessResponse(response, rowsToBeUpdated);
    } else {
      setErrorMessage(getSaveErrorMessage(response));
    }
  };

  const onCellInputBlur = (value, rowIndex, keyName) => {
    const newUpdatedData = updatedData.length ? [...updatedData] : [...data];
    if (keyName === "lastVehicle") {
      handleSelectedVehicles(rowIndex, value);
    }

    let normalizedValue;
    if (keyName === "earlyPullIn") {
      normalizedValue = value === false ? SPECIAL_FLAGS.NO : SPECIAL_FLAGS.YES;
    } else {
      normalizedValue = value.trim() === "" ? null : value;
    }

    const originalRow = data[rowIndex];
    const updatedRow = { ...newUpdatedData[rowIndex], [keyName]: normalizedValue };

    // Check if any editable field actually differs from the original data
    const hasChanges = EDITABLE_COLUMNS.some((col) => {
      const original = originalRow[col] ?? null;
      const current = updatedRow[col] ?? null;
      return original !== current;
    });

    const newRow = {
      ...updatedRow,
      updated: hasChanges,
      rowIndex,
    };

    newUpdatedData[rowIndex] = newRow;

    // If no rows have actual changes, clear updatedData entirely
    const anyUpdated = newUpdatedData.some((row) => row.updated);
    setUpdatedData(anyUpdated ? newUpdatedData : []);
  };

  const handleBoxJumpToSearch = (selectedValue) => {
    if (!selectedValue) return;
    setBoxJumpTo(selectedValue.label);
  };

  // Custom filter for react-select
  const customFilter = (option, inputValue) => {
    return option.label.startsWith(inputValue);
  };

  const onRefresh = async () => {
    setShowRefreshModal(false);
    setUpdatedData([]);
    setBoxJumpTo(null);
    setDisplayUAR(false);
    setShowAuditDetailsModal(false);
    await getData();
  };

  // Use centralized column definitions
  const columns = useMemo(
    () => getTableColumns(isSpecial, displayUAR),
    [isSpecial, displayUAR],
  );

  const tableData = updatedData.length ? updatedData : data;

  // Custom filter types for react-table
  const filterTypes = useMemo(
    () => ({
      startsWith: startsWithFilter,
    }),
    [],
  );

  // Default column configuration with startsWith filter
  const defaultColumn = useMemo(
    () => ({
      filter: "startsWith",
    }),
    [],
  );

  const tableInstance = useTable(
    {
      columns,
      data: tableData,
      defaultColumn,
      filterTypes,
      autoResetFilters: false,
      onCellInputBlur,
      rowCount: tableData.length,
      selected,
      selectedVehicles,
      handleSelectedVehicles,
      TableContext,
      viewOnly,
    },
    useFilters,
  );

  // Handle filter checkbox change
  const handleFilterChange = (event) => {
    const { name } = event.target;
    if (exceptionFilters.includes(name)) {
      setExceptionFilters(exceptionFilters.filter((filter) => filter !== name));
      return;
    }
    setExceptionFilters([...exceptionFilters, name]);
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setAllFilters,
    state: { filters: columnFilters },
  } = tableInstance;

  if (loadingData || loadingTableData) {
    return (
      <div style={{ textAlign: "center", marginTop: "1rem" }}>
        <h4 className="loading">Loading ........</h4>
      </div>
    );
  }

  return (
    <MainTableWrapper>
      <TableContext.Provider
        value={{
          vehicles,
          locations,
          tradeReasons,
          exceptions,
          duplicateVehicles,
          assignmentDate,
          pulloutDivision,
        }}
      >
        <ToolbarRow>
          <label htmlFor="BoxJumTo" style={{ whiteSpace: "nowrap" }}>
            Box Jump-To
          </label>
          <Select
            options={boxNumbers.map(({ boxNumber, sortOrder }) => ({
              value: sortOrder,
              label: boxNumber,
            }))}
            value={
              boxJumpTo
                ? (() => {
                    const match = boxNumbers.find(
                      (b) => b.boxNumber === boxJumpTo,
                    );
                    return match
                      ? { value: match.sortOrder, label: match.boxNumber }
                      : null;
                  })()
                : null
            }
            onChange={handleBoxJumpToSearch}
            onKeyDown={(e) => {
              // Backspace/Delete on the empty input clears the box selection,
              // matching the Bus/Location fields.
              if (["Backspace", "Delete"].includes(e.key) && !e.target.value) {
                setBoxJumpTo(null);
              }
            }}
            placeholder=""
            isSearchable
            isDisabled={tableFetchFailed}
            filterOption={customFilter}
            menuPortalTarget={document.body}
            styles={{
              control: (styles) => ({
                ...styles,
                width: "100px",
                minHeight: "32px",
                border: "1.5px solid #999",
                boxShadow: "0 1px 4px rgba(0, 0, 0, 0.15)",
                backgroundColor: "#fff",
                "input[type='text']:focus": { boxShadow: "none" },
                "&:hover": {
                  borderColor: "#666",
                },
              }),
              valueContainer: (base) => ({
                ...base,
                padding: "0 6px",
              }),
              input: () => ({
                visibility: "visible",
                transform: "",
                flex: "1 1 auto",
                gridArea: "1 / 1 / 2 / 3",
                margin: 2,
                paddingBottom: 2,
                paddingTop: 2,
                color: "hsl(0, 0%, 20%)",
                boxSizing: "border-box",
                border: 0,
                boxShadow: "none",
              }),
              menuPortal: (base) => ({
                ...base,
                zIndex: 9999,
              }),
            }}
          />
          <ToolbarDivider />
          <button type="button" className="filterBtn" onClick={togglePopup}>
            Audit
          </button>
          <button
            type="button"
            className="filterBtn"
            onClick={() => setShowRefreshModal(true)}
          >
            Fetch Latest
          </button>
          {(rowFilter !== "ALL" ||
            exceptionFilters.length > 0 ||
            columnFilters.length > 0) && (
            <button
              className="clearFiltersBtn"
              onClick={() => {
                clearAllFilters();
                setAllFilters([]);
              }}
              disabled={tableFetchFailed}
            >
              Clear All Filters
            </button>
          )}
          {!viewOnly && updatedData.length > 0 && (
            <>
              <button
                type="button"
                className="cancelBtn"
                disabled={isSaving}
                onClick={() => {
                  setShowCancelModal(true);
                }}
              >
                Cancel Changes
              </button>
              <button
                type="button"
                className="saveBtn"
                disabled={isSaving}
                onClick={() => {
                  handleUpdateTable();
                }}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </>
          )}
          {/* ASKED TO REMOVE UAR. COMMENTING OUT FOR NOW */}
          {/* <ToolbarDivider />
          <label style={{ display: "flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap" }}>
            <input
              checked={displayUAR}
              type="checkbox"
              onChange={() => {
                setDisplayUAR(!displayUAR);
              }}
            />
            Display UAR
          </label>
          <ToolbarDivider /> */}
          <span style={{ display: "flex", alignItems: "center", whiteSpace: "nowrap", fontWeight: 700 }}>
            Filter By Exception:
          </span>
          {["NBA", "NDA", "NRR"].map((code) => (
            <label key={code} style={{ display: "flex", alignItems: "center", gap: "3px" }}>
              <input
                type="checkbox"
                name={code}
                checked={exceptionFilters.includes(code)}
                onChange={handleFilterChange}
                disabled={tableFetchFailed}
              />
              {code}
            </label>
          ))}
          <label style={{ display: "flex", alignItems: "center", gap: "3px" }}>
            <input
              type="checkbox"
              name="vehicleTypeMismatch"
              checked={exceptionFilters.includes("vehicleTypeMismatch")}
              onChange={handleFilterChange}
              disabled={tableFetchFailed}
            />
            {"Vehicle Type Mismatch"}
          </label>
          {lastUpdated && data.length > 0 && (
            <>
              <ToolbarDivider />
              <span style={{ whiteSpace: "nowrap", fontWeight: 400 }}>
                Table Data Last Updated:{" "}
                {(() => {
                  const { text, isOverAnHour } = getRelativeTimeString(lastUpdated);
                  return (
                    <span
                      style={{
                        fontWeight: "bold",
                        color: isOverAnHour ? "red" : "inherit",
                      }}
                    >
                      {text}
                    </span>
                  );
                })()}
              </span>
            </>
          )}
        </ToolbarRow>
        {(errorMessage || successMessage || updateErrors.length > 0 || blockMessages.length > 0) && (
          <MessageGroup>
            {errorMessage && (
              <AlertBanner className="error">{errorMessage}</AlertBanner>
            )}
            {successMessage && (
              <AlertBanner className="success">{successMessage}</AlertBanner>
            )}
            {updateErrors.length > 0 && (
              <AlertBanner className="error">
                <button type="button" className="alert-link-button" onClick={() => setShowErrorModal(true)}>
                  See {updateErrors.length === 1 ? "Error" : "Errors"}
                </button>
                <ErrorModal
                  isOpen={showErrorModal}
                  onClose={() => setShowErrorModal(false)}
                  errors={updateErrors}
                />
              </AlertBanner>
            )}
            {blockMessages.length > 0 && (
              <AlertBanner className="warning">
                <button type="button" className="alert-link-button" onClick={() => setShowWarningModal(true)}>
                  See {blockMessages.length === 1 ? "Warning" : "Warnings"}
                </button>
                <WarningModal
                  isOpen={showWarningModal}
                  onClose={() => setShowWarningModal(false)}
                  warnings={blockMessages}
                />
              </AlertBanner>
            )}
            <DismissAllButton
              onClick={() => {
                setErrorMessage("");
                setSuccessMessage("");
                setBlockMessages([]);
                setUpdateErrors([]);
              }}
            >
              Clear All Messages
            </DismissAllButton>
          </MessageGroup>
        )}
        <RefreshModal
          isOpen={showRefreshModal}
          onConfirm={onRefresh}
          onCancel={() => setShowRefreshModal(false)}
          hasUnsavedChanges={updatedData.length > 0}
        />
        <CancelChangesModal
          isOpen={showCancelModal}
          onConfirm={() => {
            setShowCancelModal(false);
            setUpdatedData([]);
            setSuccessMessage("");
            setErrorMessage("");
            setBlockMessages([]);
            setUpdateErrors([]);
          }}
          onCancel={() => setShowCancelModal(false)}
        />
        {showAuditDetailsModal && (
          <AuditPopup
            showAuditDetailsModal={showAuditDetailsModal}
            setShowAuditDetailsModal={setShowAuditDetailsModal}
            pulloutDivision={pulloutDivision}
            assignmentDate={assignmentDate}
          />
        )}
        {data.length === 0 ? (
          <div style={{ textAlign: "center", marginTop: "1rem" }}>
            <h4 className="loading">No Data Available.</h4>
          </div>
        ) : (
          <>
            <TableContainer ref={tableBodyRef}>
              <table {...getTableProps()}>
                <thead>
                  {headerGroups.map((headerGroup, index) => {
                    const { key: _headerKey, ...headerGroupProps } =
                      headerGroup.getHeaderGroupProps();
                    return (
                      <tr key={_headerKey ?? index} {...headerGroupProps}>
                        {headerGroup.headers.map((column) => {
                          const { key: _colKey, ...headerProps } =
                            column.getHeaderProps();
                          return (
                            <th
                              key={_colKey ?? column.Header}
                              width={column.width}
                              {...headerProps}
                            >
                              {column.render("Header")}
                              <div>
                                {column.canFilter
                                  ? column.render("Filter")
                                  : null}
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    );
                  })}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {rows.map((row, i) => {
                    prepareRow(row);
                    const { key: _rowKey, ...rowProps } = row.getRowProps({
                      style: {
                        justifyContent: "center",
                        padding: "auto 0",
                        marginTop: "5px",
                        backgroundColor:
                          row.index === selected
                            ? "#fffee0"
                            : row.values.warningMessages.includes(
                                  "Pullout Time Past",
                                )
                              ? "#ffe6e6"
                              : row.values.warningMessages.includes(
                                    "Pullout Time Approaching",
                                  )
                                ? "#ffebcc"
                                : "",
                        border: row.values.warningMessages.includes(
                          "Pullout Time Past",
                        )
                          ? "solid 3px red"
                          : row.values.warningMessages.includes(
                                "Pullout Time Approaching",
                              )
                            ? "solid 3px orange"
                            : "solid 1px lightgrey",
                      },
                    });
                    return (
                      <tr
                        key={_rowKey ?? i}
                        ref={(el) => (rowRefs.current[i] = el)}
                        {...rowProps}
                      >
                        {row.cells.map((cell, cellIndex) => {
                          const { key: _cellKey, ...cellProps } =
                            cell.getCellProps({
                              style: {
                                margin: "auto",
                                borderTop: "none",
                                borderBottom: "none",
                                fontWeight: "bold",
                              },
                            });
                          return (
                            <td key={_cellKey ?? cellIndex} {...cellProps}>
                              {cell.render("Cell")}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableContainer>
            <TableWarnings />
          </>
        )}
      </TableContext.Provider>
    </MainTableWrapper>
  );
};

MainTable.propTypes = {
  assignmentDate: PropTypes.instanceOf(Date).isRequired,
  blockMessages: PropTypes.array.isRequired,
  boxJumpTo: PropTypes.string,
  clearAllFilters: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  errorMessage: PropTypes.string,
  exceptionFilters: PropTypes.array.isRequired,
  getData: PropTypes.func.isRequired,
  isSpecial: PropTypes.bool.isRequired,
  lastUpdated: PropTypes.instanceOf(Date),
  loadingTableData: PropTypes.bool,
  onVehicleFetchFailed: PropTypes.func,
  pulloutDivision: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  rowFilter: PropTypes.string.isRequired,
  selected: PropTypes.number.isRequired,
  selectedVehicles: PropTypes.array.isRequired,
  setApiErrors: PropTypes.func,
  setBlockMessages: PropTypes.func.isRequired,
  setBoxJumpTo: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  setExceptionFilters: PropTypes.func.isRequired,
  setSelectedVehicles: PropTypes.func.isRequired,
  setSuccessMessage: PropTypes.func.isRequired,
  setUpdateErrors: PropTypes.func.isRequired,
  setUpdatedData: PropTypes.func.isRequired,
  setVehiclesLoading: PropTypes.func,
  successMessage: PropTypes.string,
  tableFetchFailed: PropTypes.bool,
  TableContext: PropTypes.object.isRequired,
  updatedData: PropTypes.array.isRequired,
  updateErrors: PropTypes.array.isRequired,
  userDivisions: PropTypes.array.isRequired,
  vehicleDivision: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default MainTable;
